import { Extends } from '../../types/util'
import { FC } from 'react'
import { FormControlLabel, FormControlLabelProps } from '../FormControlLabel/FormControlLabel'
import { default as MuiRadio, RadioProps as MuiRadioProps } from '@mui/material/Radio'

export type RadioColor = Extends<MuiRadioProps['color'], 'default' | 'primary' | 'secondary'>
export type RadioProps = Pick<
  MuiRadioProps,
  | 'checked'
  | 'defaultChecked'
  | 'disabled'
  | 'id'
  | 'name'
  | 'onChange'
  | 'required'
  | 'size'
  | 'icon'
  | 'checkedIcon'
> &
  Pick<FormControlLabelProps, 'label' | 'value' | 'labelPlacement'> & {
    /**
     * color is applied to the `Radio` component
     * @default 'primary'
     */
    color?: RadioColor
  } & {
    hiddenLabel?: boolean
  }

/**
 * Internal Component used by RadioGroup.
 *
 * Wrapped by `FormControlLabel` to provide a label.
 * If `hiddenLabel` is true, `aria-label` is used instead,
 * but only if `label` is a string.
 */
export const Radio: FC<RadioProps> = ({
  label,
  hiddenLabel,
  labelPlacement,
  ...rest
}: RadioProps) => {
  if (hiddenLabel && typeof label !== 'string') {
    throw new Error('`hiddenLabel` can only be used with a string `label`')
  }

  return hiddenLabel && typeof label === 'string' ? (
    <MuiRadio inputProps={{ 'aria-label': label }} {...rest} />
  ) : (
    <FormControlLabel
      control={<MuiRadio {...rest} />}
      label={label}
      labelPlacement={labelPlacement}
    />
  )
}
